import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { useContacts } from '../../contexts/ContactContext';
import { useAgents } from '../../contexts/AgentContext';
import ActivityTimeline from './ActivityTimeline';
import AICallModal from './AICallModal';
import AICallSummaryTab from './AICallSummaryTab';
import CallStatusIndicator from './CallStatusIndicator';
import ContactEmailActions from './ContactEmailActions';
import SafeIcon from '../../common/SafeIcon';
import QuickAICallModal from './QuickAICallModal';
import { FiArrowRight, FiBriefcase, FiCalendar, FiCheckCircle, FiChevronDown, FiChevronLeft, FiChevronRight, FiCircle, FiClock, FiEdit, FiFileText, FiGlobe, FiLoader, FiMail, FiMapPin, FiMessageSquare, FiMic, FiPhone, FiPhoneCall, FiPlus, FiStar, FiTarget, FiTrendingUp, FiUser, FiX } from 'react-icons/fi';


const ContactDetail = ({ contactId, onClose, contactList = [], currentIndex = 0, onNavigate }) => {
  const { getContactById, updateContact, addInteraction, getContactActivities, getContactNotes, getContactTasks, getContactMeetings, dbInitialized } = useContacts();
  const { agents } = useAgents();
  const [contact, setContact] = useState(null);
  const [activities, setActivities] = useState([]);
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [showStageModal, setShowStageModal] = useState(false);
  const [showAICallModal, setShowAICallModal] = useState(false);
  const [showCallOptions, setShowCallOptions] = useState(false);
  const [showQuickCallModal, setShowQuickCallModal] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [aiCallsCount, setAiCallsCount] = useState(0);
  const [hasRemoteAiCallCount, setHasRemoteAiCallCount] = useState(false);

  // Calculate navigation state
  const canNavigatePrevious = currentIndex > 0;
  const canNavigateNext = currentIndex < contactList.length - 1;
  const totalContacts = contactList.length;
  const currentPosition = currentIndex + 1;

  // Get next/previous contact info for tooltips
  const nextContact = canNavigateNext ? contactList[currentIndex + 1] : null;
  const previousContact = canNavigatePrevious ? contactList[currentIndex - 1] : null;

  useEffect(() => {
    setHasRemoteAiCallCount(false);
    setAiCallsCount(0);
  }, [contactId]);

  useEffect(() => {
    if (!hasRemoteAiCallCount) {
      const localCount = activities?.filter(activity => activity.type === 'call_outbound_ai').length || 0;
      setAiCallsCount(localCount);
    }
  }, [activities, hasRemoteAiCallCount]);

  const handleAiCallCountChange = useCallback((count) => {
    setHasRemoteAiCallCount(true);
    setAiCallsCount(Number.isFinite(count) ? count : 0);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Ignore if user is typing in input fields
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
      }

      switch (event.key) {
        case 'ArrowLeft':
        case 'j':
          event.preventDefault();
          handlePreviousContact();
          break;
        case 'ArrowRight':
        case 'k':
          event.preventDefault();
          handleNextContact();
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex, contactList]);

  const loadContact = useCallback(async (id) => {
    setLoading(true);
    try {
      // Load contact data
      const contactData = await getContactById(id);
      if (!contactData) {
        // Fallback to mock data if not found
        const mockContact = getMockContactData(id);
        setContact(mockContact);
        setActivities(mockContact.activities || []);
        setNotes(mockContact.notes || []);
        setTasks(mockContact.tasks || []);
        setMeetings(mockContact.meetings || []);
      } else {
        setContact(contactData);
        
        // Load related data if database is configured
        if (dbInitialized) {
          const [activitiesData, notesData, tasksData, meetingsData] = await Promise.all([
            getContactActivities(id),
            getContactNotes(id),
            getContactTasks(id),
            getContactMeetings(id)
          ]);
          
          setActivities(activitiesData || []);
          setNotes(notesData || []);
          setTasks(tasksData || []);
          setMeetings(meetingsData || []);
        } else {
          // Use mock data for non-configured database
          const mockData = getMockContactData(id);
          setActivities(mockData.activities || []);
          setNotes(mockData.notes || []);
          setTasks(mockData.tasks || []);
          setMeetings(mockData.meetings || []);
        }
      }
    } catch (error) {
      console.error('Error loading contact:', error);
      // Fallback to mock data on error
      const mockContact = getMockContactData(id);
      setContact(mockContact);
      setActivities(mockContact.activities || []);
      setNotes(mockContact.notes || []);
      setTasks(mockContact.tasks || []);
      setMeetings(mockContact.meetings || []);
    } finally {
      setLoading(false);
    }
  }, [getContactById, getContactActivities, getContactNotes, getContactTasks, getContactMeetings, dbInitialized]);

  const getMockContactData = (id) => {
    // Enhanced mock data with AI call history
    const mockContacts = {
      '1': {
        id: '1',
        firstName: 'Sarah',
        lastName: 'Johnson',
        email: 'sarah.johnson@statefarm.com',
        phone: '+1 (555) 234-5678',
        company: 'State Farm Insurance',
        title: 'Life Insurance Agent'
      },
      '2': {
        id: '2',
        firstName: 'Mike',
        lastName: 'Chen',
        email: 'mike.chen@allstate.com',
        phone: '+1 (555) 345-6789',
        company: 'Allstate',
        title: 'Insurance Agent'
      },
      '3': {
        id: '3',
        firstName: 'Jennifer',
        lastName: 'Davis',
        email: 'j.davis@liberty.com',
        phone: '+1 (555) 456-7890',
        company: 'Liberty Mutual',
        title: 'Senior Agent'
      }
    };

    const baseContact = mockContacts[id] || {
      id: id,
      firstName: 'Sebastian',
      lastName: 'Rodriguez',
      email: 'sebastian.rodriguez@example.com',
      phone: '+1 (555) 000-0000',
      company: 'Insurance Corp',
      title: 'Agent'
    };

    return {
      ...baseContact,
      profileImage: null,
      recruitmentStage: 'lead',
      interestLevel: 'medium',
      leadSource: 'LinkedIn Outreach',
      dateAdded: '2024-01-15T10:30:00Z',
      lastContact: '2024-01-20T14:15:00Z',
      lastContactMethod: 'ai_call',
      nextAction: 'Follow up call scheduled',
      nextActionDate: '2024-01-25T10:00:00Z',
      location: 'Dallas, TX',
      experience: '3 years',
      currentProduction: '$120K annual premium',
      background: 'Experienced insurance professional with strong client relationships.',
      notes: [
        {
          id: 1,
          content: 'AI call completed. Prospect expressed interest in opportunity.',
          timestamp: '2024-01-20T14:15:00Z',
          author: 'Thoughtly AI',
          type: 'ai_call'
        }
      ],
      activities: [
        {
          id: '1',
          type: 'call_outbound_ai',
          title: 'AI Outreach Call - Thoughtly',
          description: 'Initial recruitment outreach via Sarah - Initial Outreach agent',
          timestamp: '2024-01-20T14:20:00Z',
          user: 'Sarah AI Agent',
          details: {
            duration: '4:45',
            callType: 'ai_outreach',
            disposition: 'interested',
            outcome: 'interested',
            agentName: 'Sarah - Initial Outreach',
            aiSummary: 'Contact answered and showed genuine interest in the opportunity. Asked detailed questions about compensation and growth potential. Requested follow-up meeting to learn more.',
            sentiment: 'positive',
            interestLevel: 'high',
            recordingUrl: 'https://thoughtly.co/recordings/abc123',
            transcriptUrl: 'https://thoughtly.co/transcripts/abc123',
            aiConfidence: 0.89
          },
          outcome: 'interested',
          priority: 'high'
        }
      ],
      tasks: [
        {
          id: 1,
          title: 'Schedule overview meeting',
          description: 'Book 30-minute call with John Smith to discuss opportunity details',
          dueDate: '2024-01-25T10:00:00Z',
          status: 'pending',
          priority: 'high'
        }
      ],
      meetings: []
    };
  };

  useEffect(() => {
    if (contactId) {
      loadContact(contactId);
    }
  }, [contactId, loadContact]);

  const handlePreviousContact = async () => {
    if (!canNavigatePrevious || isTransitioning) return;
    
    setIsTransitioning(true);
    const newIndex = currentIndex - 1;
    const newContactId = contactList[newIndex].id;
    
    try {
      if (onNavigate) {
        await onNavigate(newContactId, newIndex);
      }
    } finally {
      setIsTransitioning(false);
    }
  };

  const handleNextContact = async () => {
    if (!canNavigateNext || isTransitioning) return;
    
    setIsTransitioning(true);
    const newIndex = currentIndex + 1;
    const newContactId = contactList[newIndex].id;
    
    try {
      if (onNavigate) {
        await onNavigate(newContactId, newIndex);
      }
    } finally {
      setIsTransitioning(false);
    }
  };

  const handleAICallInitiated = (callData) => {
    setActiveCall(callData);
    setShowAICallModal(false);
    setAiCallsCount((previous) => (Number.isFinite(previous) ? previous + 1 : 1));

    // Add activity to timeline
    const aiCallActivity = {
      id: Date.now().toString(),
      type: 'call_outbound_ai',
      title: `AI Call Initiated - ${callData.agentName}`,
      description: `Calling ${contact.phone} using ${callData.agentName}`,
      timestamp: new Date().toISOString(),
      user: callData.agentName,
      details: {
        duration: '0:00',
        callType: 'ai_outreach',
        disposition: 'in_progress',
        outcome: 'in_progress',
        agentName: callData.agentName,
        provider: callData.provider || 'thoughtly',
        providerResponse: callData.providerResponse || null,
        callId: callData.callId || callData.id,
        responseId:
          callData.responseId ||
          callData.providerResponse?.response_id ||
          callData.providerResponse?.responseId ||
          callData.providerResponse?.data?.response_id ||
          callData.providerResponse?.data?.responseId ||
          null
      },
      outcome: 'in_progress',
      priority: 'high'
    };

    if (addInteraction) {
      addInteraction(contact.id, aiCallActivity);
    }
  };

  const handleCallComplete = (callResult) => {
    // Update the contact with call results
    if (updateContact) {
      updateContact(contact.id, {
        lastContact: callResult.completedAt,
        lastContactMethod: 'ai_call',
        recruitmentStage: callResult.aiAnalysis?.recommendedStage || contact.recruitmentStage
      });
    }

    // Add completed call to activity timeline
    const completedCallActivity = {
      id: Date.now().toString(),
      type: 'call_outbound_ai',
      title: `AI Call Completed - ${callResult.agentName}`,
      description: `Call outcome: ${callResult.outcome}`,
      timestamp: callResult.completedAt,
      user: callResult.agentName,
      details: {
        duration: callResult.duration,
        callType: 'ai_outreach',
        disposition: callResult.outcome,
        outcome: callResult.outcome,
        agentName: callResult.agentName,
        aiSummary: `Call completed with outcome: ${callResult.outcome}. Duration: ${callResult.duration}`,
        transcript: callResult.transcript,
        aiAnalysis: callResult.aiAnalysis,
        sentiment: callResult.aiAnalysis?.sentiment?.toLowerCase() || 'neutral',
        interestLevel: callResult.outcome === 'interested' ? 'high' : 'medium',
        aiConfidence: callResult.aiAnalysis?.confidence || 0.75,
        callId: callResult.callId || callResult.id,
        responseId:
          callResult.responseId ||
          callResult.providerResponse?.response_id ||
          callResult.providerResponse?.responseId ||
          callResult.providerResponse?.data?.response_id ||
          callResult.providerResponse?.data?.responseId ||
          null,
        providerResponse: callResult.providerResponse || null
      },
      outcome: callResult.outcome,
      priority: callResult.outcome === 'interested' ? 'high' : 'medium'
    };

    if (addInteraction) {
      addInteraction(contact.id, completedCallActivity);
    }

    setActiveCall(null);
    
    // Reload contact to show updated data
    loadContact(contactId);
  };

  const handleCallCancel = () => {
    setActiveCall(null);
  };

  const handleEmailSent = (emailActivity) => {
    if (addInteraction) {
      addInteraction(contact.id, emailActivity);
    }
    
    // Update contact's last contact info
    if (updateContact) {
      updateContact(contact.id, {
        lastContact: new Date().toISOString(),
        lastContactMethod: 'email'
      });
    }
    
    // Reload contact to show updated data
    loadContact(contactId);
  };

  const getSuggestedAgent = () => {
    const stage = contact?.recruitmentStage || 'lead';
    const activeAgents = agents.filter(agent => agent.status === 'active');
    
    const stageMapping = {
      'lead': 'initial_outreach',
      'contacted': 'follow_up',
      'interested': 'follow_up',
      'nurturing': 'reactivation',
      'meeting_scheduled': 'meeting_reminder'
    };
    
    const suggestedScenario = stageMapping[stage] || 'initial_outreach';
    return activeAgents.find(agent => agent.scenario === suggestedScenario) || activeAgents[0];
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl p-8">
          <div className="flex items-center justify-center">
            <SafeIcon icon={FiLoader} className="animate-spin text-blue-600 text-2xl mr-3" />
            <span className="ml-3 text-gray-600">Loading contact details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Contact Not Found</h3>
          <p className="text-gray-600 mb-4">The contact you're looking for could not be found.</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const recruitmentStages = [
    { key: 'lead', label: 'Lead', color: 'gray' },
    { key: 'contacted', label: 'Contacted', color: 'blue' },
    { key: 'interested', label: 'Interested', color: 'green' },
    { key: 'nurturing', label: 'Nurturing', color: 'yellow' },
    { key: 'meeting_scheduled', label: 'Meeting Scheduled', color: 'purple' },
    { key: 'converted', label: 'Converted', color: 'emerald' },
    { key: 'not_interested', label: 'Not Interested', color: 'red' }
  ];

  const currentStageIndex = recruitmentStages.findIndex(stage => stage.key === contact.recruitmentStage);

  const getStageColor = (stage) => {
    const colors = {
      gray: 'bg-gray-100 text-gray-800',
      blue: 'bg-blue-100 text-blue-800',
      green: 'bg-green-100 text-green-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      purple: 'bg-purple-100 text-purple-800',
      emerald: 'bg-emerald-100 text-emerald-800',
      red: 'bg-red-100 text-red-800'
    };
    return colors[stage] || colors.gray;
  };

  const getInterestColor = (level) => {
    const colors = {
      high: 'text-green-600 bg-green-50',
      medium: 'text-yellow-600 bg-yellow-50',
      low: 'text-red-600 bg-red-50',
      unknown: 'text-gray-600 bg-gray-50'
    };
    return colors[level] || colors.unknown;
  };

  const handleQuickAction = (action) => {
    console.log(`Quick action: ${action}`);
    if (addInteraction) {
      addInteraction(contact.id, {
        type: action,
        description: `Quick action: ${action}`,
        timestamp: new Date().toISOString()
      });
    }
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      <div className="bg-gray-50 rounded-lg p-6">
        <h4 className="font-semibold text-gray-900 mb-4">Basic Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-500">Current Role</label>
            <p className="font-medium">{contact.title}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Company</label>
            <p className="font-medium">{contact.company}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Experience</label>
            <p className="font-medium">{contact.experience || 'Not specified'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Current Production</label>
            <p className="font-medium">{contact.currentProduction || 'Not specified'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Location</label>
            <p className="font-medium flex items-center">
              <SafeIcon icon={FiMapPin} className="text-gray-400 mr-1 text-sm" />
              {contact.location || 'Not specified'}
            </p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Lead Source</label>
            <p className="font-medium">{contact.leadSource || 'Not specified'}</p>
          </div>
        </div>
      </div>

      {contact.background && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h4 className="font-semibold text-gray-900 mb-3">Background</h4>
          <p className="text-gray-700 leading-relaxed">{contact.background}</p>
        </div>
      )}

      <div className="bg-blue-50 rounded-lg p-6">
        <h4 className="font-semibold text-gray-900 mb-4">Recruitment Insights</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getInterestColor(contact.interestLevel || 'unknown')}`}>
              <SafeIcon icon={FiStar} className="mr-1 text-xs" />
              {contact.interestLevel ? contact.interestLevel.charAt(0).toUpperCase() + contact.interestLevel.slice(1) : 'Unknown'} Interest
            </div>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-500">Days in Pipeline</p>
            <p className="text-lg font-bold text-gray-900">
              {contact.dateAdded ? Math.floor((new Date() - new Date(contact.dateAdded)) / (1000 * 60 * 60 * 24)) : 0}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-500">Total Interactions</p>
            <p className="text-lg font-bold text-gray-900">{activities?.length || 1}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderActivityTab = () => (
    <ActivityTimeline
      contactId={contactId}
      activities={activities}
    />
  );

  const renderAICallSummaryTab = () => (
    <AICallSummaryTab
      contact={contact}
      agents={agents}
      onMakeAICall={() => setShowAICallModal(true)}
      isActive={activeTab === 'ai_calls'}
      onCallCountChange={handleAiCallCountChange}
    />
  );

  const renderNotesTab = () => (
    <div className="space-y-4">
      <button
        onClick={() => handleQuickAction('add_note')}
        className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
      >
        <SafeIcon icon={FiPlus} />
        Add Note
      </button>

      {notes && notes.length > 0 ? (
        notes.map((note, index) => (
          <motion.div
            key={note.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-yellow-50 rounded-lg p-4 border border-yellow-200"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiMessageSquare} className="text-yellow-600" />
                <span className="font-medium text-gray-900">{note.author}</span>
              </div>
              <span className="text-xs text-gray-500">
                {format(new Date(note.timestamp), 'MMM d, yyyy h:mm a')}
              </span>
            </div>
            <p className="text-gray-700 leading-relaxed">{note.content}</p>
          </motion.div>
        ))
      ) : (
        <div className="text-center py-8 text-gray-500">
          <SafeIcon icon={FiMessageSquare} className="text-4xl mx-auto mb-4 text-gray-300" />
          <p>No notes added yet</p>
        </div>
      )}
    </div>
  );

  const renderTasksTab = () => (
    <div className="space-y-4">
      <button
        onClick={() => handleQuickAction('create_task')}
        className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
      >
        <SafeIcon icon={FiPlus} />
        Create Task
      </button>

      {tasks && tasks.length > 0 ? (
        tasks.map((task, index) => (
          <motion.div
            key={task.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`rounded-lg p-4 border ${
              task.status === 'completed' ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <SafeIcon
                  icon={task.status === 'completed' ? FiCheckCircle : FiCircle}
                  className={task.status === 'completed' ? 'text-green-600' : 'text-gray-400'}
                />
                <div className="flex-1">
                  <h5 className={`font-medium ${task.status === 'completed' ? 'text-green-900 line-through' : 'text-gray-900'}`}>
                    {task.title}
                  </h5>
                  <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-xs text-gray-500">
                      Due: {format(new Date(task.dueDate), 'MMM d, yyyy')}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      task.priority === 'high' ? 'bg-red-100 text-red-800' :
                      task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {task.priority} priority
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))
      ) : (
        <div className="text-center py-8 text-gray-500">
          <SafeIcon icon={FiTarget} className="text-4xl mx-auto mb-4 text-gray-300" />
          <p>No tasks created yet</p>
        </div>
      )}
    </div>
  );

  const renderMeetingsTab = () => (
    <div className="space-y-4">
      <button
        onClick={() => handleQuickAction('schedule_meeting')}
        className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
      >
        <SafeIcon icon={FiCalendar} />
        Schedule Meeting
      </button>

      {meetings && meetings.length > 0 ? (
        meetings.map((meeting, index) => (
          <motion.div
            key={meeting.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-purple-50 rounded-lg p-4 border border-purple-200"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h5 className="font-medium text-gray-900 mb-1">{meeting.title}</h5>
                <p className="text-sm text-gray-600 mb-3">{meeting.description}</p>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <SafeIcon icon={FiCalendar} className="text-xs" />
                    {format(new Date(meeting.scheduledDate), 'MMM d, yyyy h:mm a')}
                  </span>
                  <span className="flex items-center gap-1">
                    <SafeIcon icon={FiClock} className="text-xs" />
                    {meeting.duration}
                  </span>
                </div>
                {meeting.meetingLink && (
                  <a
                    href={meeting.meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-blue-600 hover:text-blue-700 text-sm"
                  >
                    Join Meeting
                    <SafeIcon icon={FiArrowRight} className="text-xs" />
                  </a>
                )}
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${
                meeting.status === 'scheduled' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {meeting.status}
              </span>
            </div>
          </motion.div>
        ))
      ) : (
        <div className="text-center py-8 text-gray-500">
          <SafeIcon icon={FiCalendar} className="text-4xl mx-auto mb-4 text-gray-300" />
          <p>No meetings scheduled yet</p>
        </div>
      )}
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiUser },
    { id: 'activity', label: 'Activity Timeline', icon: FiClock },
    { id: 'ai_calls', label: 'AI Caller Summary', icon: FiMic, badge: aiCallsCount > 0 ? aiCallsCount : null },
    { id: 'notes', label: 'Notes', icon: FiMessageSquare },
    { id: 'tasks', label: 'Tasks', icon: FiTarget },
    { id: 'meetings', label: 'Meetings', icon: FiCalendar },
    { id: 'documents', label: 'Documents', icon: FiFileText }
  ];

  const suggestedAgent = getSuggestedAgent();

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <motion.div
          key={contactId}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4 flex-1">
                {/* Navigation Controls */}
                <div className="flex items-center gap-2 mr-4">
                  <button
                    onClick={handlePreviousContact}
                    disabled={!canNavigatePrevious || isTransitioning}
                    className={`p-2 rounded-lg transition-colors ${
                      canNavigatePrevious && !isTransitioning
                        ? 'hover:bg-white hover:bg-opacity-20 text-white'
                        : 'text-white text-opacity-50 cursor-not-allowed'
                    }`}
                    title={canNavigatePrevious ? `Previous Contact: ${previousContact?.firstName} ${previousContact?.lastName} (←)` : 'No previous contact'}
                  >
                    {isTransitioning ? (
                      <SafeIcon icon={FiLoader} className="animate-spin text-lg" />
                    ) : (
                      <SafeIcon icon={FiChevronLeft} className="text-lg" />
                    )}
                  </button>
                  <button
                    onClick={handleNextContact}
                    disabled={!canNavigateNext || isTransitioning}
                    className={`p-2 rounded-lg transition-colors ${
                      canNavigateNext && !isTransitioning
                        ? 'hover:bg-white hover:bg-opacity-20 text-white'
                        : 'text-white text-opacity-50 cursor-not-allowed'
                    }`}
                    title={canNavigateNext ? `Next Contact: ${nextContact?.firstName} ${nextContact?.lastName} (→)` : 'No next contact'}
                  >
                    {isTransitioning ? (
                      <SafeIcon icon={FiLoader} className="animate-spin text-lg" />
                    ) : (
                      <SafeIcon icon={FiChevronRight} className="text-lg" />
                    )}
                  </button>
                </div>

                {/* Profile Photo */}
                <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold">
                    {contact.firstName?.[0]}{contact.lastName?.[0]}
                  </span>
                </div>

                {/* Contact Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h2 className="text-2xl font-bold">
                      {contact.firstName} {contact.lastName}
                    </h2>
                    {totalContacts > 0 && (
                      <span className="text-sm bg-white bg-opacity-20 px-3 py-1 rounded-full">
                        {currentPosition} of {totalContacts}
                      </span>
                    )}
                  </div>
                  <p className="text-blue-100 mb-2">{contact.title} at {contact.company}</p>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <button
                      onClick={() => window.location.href = `tel:${contact.phone}`}
                      className="flex items-center gap-1 text-blue-100 hover:text-white transition-colors"
                    >
                      <SafeIcon icon={FiPhone} />
                      {contact.phone}
                    </button>
                    <button
                      onClick={() => window.location.href = `mailto:${contact.email}`}
                      className="flex items-center gap-1 text-blue-100 hover:text-white transition-colors"
                    >
                      <SafeIcon icon={FiMail} />
                      {contact.email}
                    </button>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>

            {/* Pipeline Progress */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Recruitment Pipeline</span>
                <button
                  onClick={() => setShowStageModal(true)}
                  className="text-sm bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded transition-colors"
                >
                  Move Stage
                </button>
              </div>
              <div className="flex items-center gap-2">
                {recruitmentStages.slice(0, -1).map((stage, index) => (
                  <React.Fragment key={stage.key}>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                      index <= currentStageIndex ? 'bg-white text-blue-600' : 'bg-white bg-opacity-20 text-blue-200'
                    }`}>
                      <SafeIcon icon={index <= currentStageIndex ? FiCheckCircle : FiCircle} className="text-xs" />
                      {stage.label}
                    </div>
                    {index < recruitmentStages.length - 2 && (
                      <SafeIcon icon={FiArrowRight} className="text-blue-300" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Keyboard Shortcuts Hint */}
            {totalContacts > 1 && (
              <div className="mt-4 text-xs text-blue-200 text-center">
                Use ← → arrow keys or J/K keys to navigate between contacts
              </div>
            )}
          </div>

          {/* Status Bar */}
          <div className="bg-gray-50 px-6 py-4 border-b">
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Stage:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStageColor(recruitmentStages[currentStageIndex]?.color)}`}>
                  {recruitmentStages[currentStageIndex]?.label}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Interest:</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getInterestColor(contact.interestLevel)}`}>
                  {contact.interestLevel ? contact.interestLevel.charAt(0).toUpperCase() + contact.interestLevel.slice(1) : 'Unknown'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Last Contact:</span>
                <span className="font-medium">
                  {contact.lastContact ? format(new Date(contact.lastContact), 'MMM d, yyyy') : 'Never'}
                  {contact.lastContactMethod && ` (${contact.lastContactMethod === 'ai_call' ? 'AI Call' : contact.lastContactMethod})`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Next Action:</span>
                <span className="font-medium">{contact.nextAction || 'None scheduled'}</span>
              </div>
            </div>
          </div>

          {/* Enhanced Quick Actions */}
          <div className="px-6 py-4 bg-white border-b">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handleQuickAction('schedule_meeting')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <SafeIcon icon={FiCalendar} className="text-sm" />
                Schedule Meeting
              </button>

              {/* Quick AI Call Button */}
              <button
                onClick={() => setShowQuickCallModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
              >
                <SafeIcon icon={FiMic} className="text-sm" />
                Quick AI Call
              </button>

              {/* Enhanced AI Call Button */}
              <div className="relative">
                <button
                  onClick={() => setShowCallOptions(!showCallOptions)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <SafeIcon icon={FiMic} className="text-sm" />
                  AI Call
                  <SafeIcon icon={FiChevronDown} className="text-sm" />
                </button>

                <AnimatePresence>
                  {showCallOptions && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-10"
                    >
                      <div className="p-2">
                        {suggestedAgent && (
                          <button
                            onClick={() => {
                              setShowAICallModal(true);
                              setShowCallOptions(false);
                            }}
                            className="w-full text-left p-3 hover:bg-purple-50 rounded-lg transition-colors border-b border-gray-100"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                                <SafeIcon icon={FiMic} className="text-white text-sm" />
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-gray-900 text-sm">
                                  {suggestedAgent.name}
                                </p>
                                <p className="text-xs text-purple-600">
                                  Recommended for {contact.recruitmentStage?.replace('_', ' ') || 'lead'} stage
                                </p>
                              </div>
                            </div>
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setShowAICallModal(true);
                            setShowCallOptions(false);
                          }}
                          className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <SafeIcon icon={FiPhoneCall} className="text-gray-400" />
                            <span className="text-sm text-gray-700">Choose Different Agent</span>
                          </div>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Email Actions */}
              <ContactEmailActions
                contact={contact}
                onEmailSent={handleEmailSent}
              />

              <button
                onClick={() => handleQuickAction('log_manual_call')}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <SafeIcon icon={FiPhone} className="text-sm" />
                Log Manual Call
              </button>

              <button
                onClick={() => handleQuickAction('add_note')}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <SafeIcon icon={FiMessageSquare} className="text-sm" />
                Add Note
              </button>

              <button
                onClick={() => handleQuickAction('create_task')}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <SafeIcon icon={FiTarget} className="text-sm" />
                Create Task
              </button>

              <button
                onClick={() => handleQuickAction('edit_contact')}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <SafeIcon icon={FiEdit} className="text-sm" />
                Edit
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b">
            <div className="flex overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 font-medium text-sm transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <SafeIcon icon={tab.icon} className="text-sm" />
                  {tab.label}
                  {tab.badge && (
                    <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full font-medium">
                      {tab.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={`${activeTab}-${contactId}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                {activeTab === 'overview' && renderOverviewTab()}
                {activeTab === 'activity' && renderActivityTab()}
                {activeTab === 'ai_calls' && renderAICallSummaryTab()}
                {activeTab === 'notes' && renderNotesTab()}
                {activeTab === 'tasks' && renderTasksTab()}
                {activeTab === 'meetings' && renderMeetingsTab()}
                {activeTab === 'documents' && (
                  <div className="text-center py-12 text-gray-500">
                    <SafeIcon icon={FiFileText} className="text-4xl mx-auto mb-4 text-gray-300" />
                    <p>No documents uploaded yet</p>
                    <button className="mt-4 text-blue-600 hover:text-blue-700 font-medium">
                      Upload Document
                    </button>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </div>

      {/* Quick AI Call Modal */}
      {showQuickCallModal && (
        <QuickAICallModal
          contact={contact}
          agent={suggestedAgent}
          onClose={() => setShowQuickCallModal(false)}
          onCallInitiated={(callData) => {
            handleAICallInitiated(callData);
            setShowQuickCallModal(false);
          }}
        />
      )}

      {/* AI Call Modal */}
      {showAICallModal && (
        <AICallModal
          contact={contact}
          onClose={() => setShowAICallModal(false)}
          onCallInitiated={handleAICallInitiated}
        />
      )}

      {/* Call Status Indicator */}
      {activeCall && (
        <CallStatusIndicator
          callData={activeCall}
          onCallComplete={handleCallComplete}
          onCallCancel={handleCallCancel}
        />
      )}
    </>
  );
};

export default ContactDetail;